// src/lib/accueil-menages.js — LE DÉTAIL DÉPLIÉ DU BANDEAU.
//
// ⚠️ MODULE PUR — trois listes : les ménages du jour, les arrivées, les
// départs. Le bandeau ne fait que les rendre.
//
// ⚠️⚠️ LA DISTINCTION LA PLUS IMPORTANTE DE L'ÉCRAN (Dimitri) :
//   · PLEIN     = solide — le voyageur a rempli son formulaire, l'heure est
//                 annoncée par lui ;
//   · POINTILLÉS = ça peut encore bouger — c'est l'heure du contrat, celle du
//                 logement, personne ne l'a confirmée.
// Elle se reprend partout. Confondre les deux, c'est promettre une heure que
// personne n'a donnée.

import { heureCourte } from './accueil-attend'
import { menageRunLockDateIso } from './menage-status-helpers'

export const TON_LIGNE = Object.freeze({
  VERT: 'vert', TERRA: 'terra', AMBRE: 'ambre', CIEL: 'ciel',
})

function texte(v) { return typeof v === 'string' ? v.trim() : '' }

/**
 * L'heure d'une ligne : sa valeur, sa source, et la FORME qui la dit.
 * `sur` = true → pastille blanche pleine ; false → cadre en pointillés.
 * Rien à dire → null (jamais une pastille vide).
 */
export function heureDeLigne({ reelle, contrat }) {
  const r = heureCourte(reelle)
  if (r) return { heure: r, source: 'annoncée', sur: true }
  const c = heureCourte(contrat)
  if (c) return { heure: c, source: 'au contrat', sur: false }
  return null
}

/**
 * L'ŒIL — cinq états, dans cet ordre de décision.
 * ⚠️ Un contrôle PASSÉ prime sur tout : il dit ce qui a été vu.
 * ⚠️ Hors de la fenêtre 48 h, l'œil est barré — le contrôle n'est plus ouvert,
 *    et prétendre qu'il « reste à faire » serait faux.
 */
export function etatOeil(run, controle, { todayIso } = {}) {
  const st = controle && controle.statut
  if (st === 'bon' || st === 'regle') return 'rien_a_signaler'
  if (st === 'a_reprendre' || st === 'reprise_en_cours') return 'a_reprendre'
  const verrou = menageRunLockDateIso(run && run.scheduled_date)
  if (verrou && todayIso && todayIso >= verrou) return 'hors_fenetre'
  return texte(run && run.controleur_designe_email) ? 'quelquun_dessus' : 'personne_dessus'
}

/**
 * LE TRIANGLE — et ⚠️ AUCUN incident rend `null` : pas d'icône éteinte.
 * `en_cours` = déclaré, pas encore jugé → à juger. Tout le reste est réglé.
 */
export function etatIncident(run, incidents) {
  const liste = (incidents || []).filter((i) => i && i.run_id === (run && run.id))
  if (!liste.length) return null
  return liste.some((i) => i.status === 'en_cours') ? 'a_juger' : 'regle'
}

/**
 * LES MÉNAGES DU JOUR — une ligne par ménage, dans l'ordre de l'heure.
 *
 * ⚠️ « personne sur ce ménage » quand il n'a pas d'agent, SANS phrase
 * d'habitude : la table des règles est vide depuis la purge, elle mentirait.
 */
export function lignesMenages(runs, {
  todayIso, nomLogement, prenomDe, heureDe, controles, incidents,
} = {}) {
  return (runs || [])
    .filter((r) => r && r.scheduled_date === todayIso)
    .map((r) => {
      const agent = texte(r.assigned_provider_email)
      const prenom = agent ? ((prenomDe && prenomDe(agent)) || agent.split('@')[0]) : null
      const ctrl = controles && controles[r.id]
      return {
        cle: `menage:${r.id}`,
        runId: r.id,
        // ⚠️ La couleur DIT la nature : bleu = un agent est dessus,
        // terracotta = personne. C'est la seule information portée par la
        // pastille — pas une décoration.
        ton: agent ? TON_LIGNE.CIEL : TON_LIGNE.TERRA,
        pastille: prenom ? prenom.slice(0, 2).toUpperCase() : '—',
        titre: (nomLogement && nomLogement(r.logement_id)) || r.logement_id,
        sousTitre: [prenom || 'personne sur ce ménage', heureDe ? heureDe(r) : null]
          .filter(Boolean).join(' · '),
        valide: r.status === 'completed',
        oeil: etatOeil(r, ctrl, { todayIso }),
        incident: etatIncident(r, incidents),
        cible: { ecran: 'menages', runId: r.id },
      }
    })
    .sort((a, b) => a.titre.localeCompare(b.titre, 'fr'))
}

/** Le nom du voyageur d'un mouvement — « — » plutôt qu'un blanc muet. */
function nomVoyageur(m) {
  const n = [texte(m.guest_first_name), texte(m.guest_last_name)].filter(Boolean).join(' ')
  return n || 'Voyageur'
}

/**
 * LES ARRIVÉES — vert. L'heure est À DROITE, en pastille, jamais dans la
 * sous-ligne.
 */
export function lignesArrivees(checkins, { nomLogement, heureContratDe } = {}) {
  return (checkins || []).map((m) => ({
    cle: `arrivee:${m.id}`,
    ton: TON_LIGNE.VERT,
    pastille: '↓',
    titre: (nomLogement && nomLogement(m.logement_id)) || m.logement_id,
    sousTitre: nomVoyageur(m),
    heure: heureDeLigne({
      reelle: m.guest_arrival_time || m.arrival_time,
      contrat: heureContratDe ? heureContratDe(m.logement_id, 'checkin') : null,
    }),
    cible: { ecran: 'calendrier', resaId: m.id, kind: m.kind },
  }))
}

/** LES DÉPARTS — terracotta. Même règle d'heure. */
export function lignesDeparts(checkouts, { nomLogement, heureContratDe } = {}) {
  return (checkouts || []).map((m) => ({
    cle: `depart:${m.id}`,
    ton: TON_LIGNE.TERRA,
    pastille: '↑',
    titre: (nomLogement && nomLogement(m.logement_id)) || m.logement_id,
    sousTitre: nomVoyageur(m),
    heure: heureDeLigne({
      reelle: m.guest_departure_time,
      contrat: heureContratDe ? heureContratDe(m.logement_id, 'checkout') : null,
    }),
    cible: { ecran: 'calendrier', resaId: m.id, kind: m.kind },
  }))
}
